import React from 'react';
import {
  Badge,
  type BadgeVariant,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
} from '@chatsdet/ui';
import { CalendarBlank, Clock } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import {
  describeScheduleTarget,
  useSuiteSchedules,
} from '../../../features/test-suites/useSuiteSchedules';
import type { TestSuiteSchedule } from '../../../features/test-suites/types';

const MAX_ROWS = 5;

/** Last automation-execution status → pill variant. */
const lastRunBadge: Record<string, { label: string; variant: BadgeVariant }> = {
  completed: { label: 'Passed', variant: 'status-success' },
  failed: { label: 'Failed', variant: 'status-failed' },
  running: { label: 'Running', variant: 'status-running' },
  queued: { label: 'Queued', variant: 'status-queued' },
  scheduled: { label: 'Scheduled', variant: 'status-queued' },
};

function nextRunLabel(nextRunAt: string | null): string {
  if (!nextRunAt) return 'Schedule paused';
  return `Next ${formatDistanceToNow(new Date(nextRunAt), { addSuffix: true })}`;
}

export function ScheduledRuns({ className }: { className?: string }) {
  const navigate = useNavigate();
  const query = useSuiteSchedules();

  const schedules: TestSuiteSchedule[] = React.useMemo(() => {
    const all = query.data?.schedules ?? [];
    return all
      .filter((s) => s.enabled)
      .sort((a, b) => {
        // Soonest next-run first; schedules without a next-run sink to the bottom.
        if (!a.nextRunAt) return 1;
        if (!b.nextRunAt) return -1;
        return new Date(a.nextRunAt).getTime() - new Date(b.nextRunAt).getTime();
      })
      .slice(0, MAX_ROWS);
  }, [query.data]);

  let body: React.ReactNode;
  if (query.isLoading) {
    body = (
      <Flex align="center" justify="center" className="py-8">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  } else if (query.isError) {
    body = (
      <Flex direction="column" gap="md" align="start" className="py-2">
        <Text size="sm" className="text-muted-foreground">
          Unable to load schedules right now.
        </Text>
        <Button variant="outline" onPress={() => query.refetch()}>
          Retry
        </Button>
      </Flex>
    );
  } else if (schedules.length === 0) {
    body = (
      <EmptyState
        icon={<CalendarBlank size={28} />}
        title="No scheduled runs"
        description="Schedule a suite to run your regression tests on a cadence."
        size="sm"
      />
    );
  } else {
    body = (
      <div className="flex flex-col">
        {schedules.map((schedule) => {
          const badge = schedule.lastRunStatus ? lastRunBadge[schedule.lastRunStatus] : undefined;
          return (
            <div
              key={schedule.ruleId}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <Text size="sm" weight="medium" className="truncate text-foreground">
                  {describeScheduleTarget(schedule)}
                </Text>
                <Text size="xs" className="text-muted-foreground">
                  {nextRunLabel(schedule.nextRunAt)}
                </Text>
              </div>
              {badge && (
                <Badge variant={badge.variant} className="shrink-0">
                  {badge.label}
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Card padding="lg" className={className}>
      <CardHeader>
        <Flex align="center" justify="between">
          <Flex align="center" gap="sm">
            <Clock size={18} />
            <Heading level={4}>Scheduled runs</Heading>
          </Flex>
          {schedules.length > 0 && (
            <Button variant="ghost" size="sm" onPress={() => navigate({ to: '/runs' })}>
              View all
            </Button>
          )}
        </Flex>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
